<script lang="ts">
import { useVirtualList } from '@vueuse/core';

import type { DetermineMultiOrSingleValue, KaListBoxEmit, KaListBoxProps } from './types';

export type { DetermineMultiOrSingleValue, KaListBoxEmit, KaListBoxProps } from './types';

type ExtractNullableValue<
  Multiple extends boolean,
  Clearable extends boolean,
> = Clearable extends true ? (Multiple extends false ? null : never) : never;

type ExtractValueType<
  Multiple extends boolean,
  MultiOrSingleValue extends DetermineMultiOrSingleValue<boolean>,
> = Multiple extends true
  ? MultiOrSingleValue extends Array<infer R>
    ? R
    : MultiOrSingleValue
  : MultiOrSingleValue;
</script>
<script
  setup
  lang="ts"
  generic="
    MultiOrSingleValue extends DetermineMultiOrSingleValue<Multiple>,
    Clearable extends boolean = true,
    Multiple extends boolean = false
  "
>
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue';
import {
  computed,
  Fragment,
  type Ref,
  ref,
  type RendererElement,
  type RendererNode,
  shallowRef,
  toRef,
  useAttrs,
  type VNode,
  type VNodeArrayChildren,
  watch,
} from 'vue';

import type {
  KaListboxOptionProps,
  KaListboxOptionValueType,
} from '@/kasten-components/components';
// ! For some reason combining the component imports causes a build failure in this component
import { KaBadge } from '@/kasten-components/components/KaBadge';
import { KaDescription } from '@/kasten-components/components/KaDescription';
import { injectListboxPortalContext } from '@/kasten-components/components/KaListbox/context';
import { KaMessage } from '@/kasten-components/components/KaMessage';
import { getText, useFloatingDropdown } from '@/kasten-components/composables';
import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import type { NoInfer, WithDefaults } from '@/kasten-components/types/tsUtils';
import { unwrapElement } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';
import ChevronUpDownIcon from '~icons/heroicons/chevron-up-down';
import IconClose from '~icons/ion/close-outline';

import KaListboxOption from './KaListboxOption.vue';
import ListboxContextProvider from './ListboxContextProvider.vue';

type InferOption<
  Multiple extends boolean,
  Clearable extends boolean,
  MultiOrSingleValue extends KaListboxOptionValueType,
> =
  KaListboxOptionProps<MultiOrSingleValue> extends KaListboxOptionProps<infer R>
    ? | (Multiple extends true ? Array<KaListboxOptionProps<R>> : KaListboxOptionProps<R>)
      | ExtractNullableValue<Multiple, Clearable>
    : never;

type ExtractedValueType = ExtractValueType<NoInfer<Multiple>, NoInfer<MultiOrSingleValue>>;

const isMultiValue = function <SingleValueT>(
  value: SingleValueT | SingleValueT[],
  isMultiple: boolean,
): value is SingleValueT[] {
  return isMultiple;
};

const normalizeSelectedValue = (
  value?: MultiOrSingleValue | ExtractNullableValue<Multiple, Clearable>,
) =>
  (value ?? (multiple.value ? [] : null)) as
    MultiOrSingleValue | ExtractNullableValue<Multiple, Clearable>;

const portalContext = injectListboxPortalContext();

defineOptions({
  name: 'KaListbox',
});

const attrs = useAttrs();

type Props = KaListBoxProps<Multiple, Clearable, MultiOrSingleValue>;

const props = withDefaults(defineProps<Props>(), {
  // Set modelValue to undefined explicitly because vue by default sets booleans to false.
  modelValue: undefined,
  description: undefined,
  hideLabel: false,
  hideArrow: false,
  // TS yields error because these fields are linked to generic. Probably they'll fix it later.
  // @ts-expect-error
  clearable: true,
  // @ts-expect-error
  multiple: false,
  renderOptionsInPlace: false,
  showErrorStylesOnly: false,
}) as WithDefaults<Props, 'hideLabel' | 'clearable' | 'renderOptionsInPlace'>;

const multiple = computed(() => !!props.multiple);

const emit = defineEmits<KaListBoxEmit<Multiple, Clearable, MultiOrSingleValue>>();
const slots = defineSlots<{
  default?(props: Record<string, never>): VNode[];
  optionItem?(props: {
    index: number;
    option: KaListboxOptionProps<ExtractValueType<NoInfer<Multiple>, NoInfer<MultiOrSingleValue>>>;
  }): VNode[];
  label?(props: { label: string }): VNode[];
  description?(props: { description?: string }): VNode[];
  left?(props: {
    selected: InferOption<
      Multiple,
      Clearable,
      ExtractValueType<NoInfer<Multiple>, NoInfer<MultiOrSingleValue>>
    >;
  }): VNode[];
  right?(props: {
    selected: InferOption<
      Multiple,
      Clearable,
      ExtractValueType<NoInfer<Multiple>, NoInfer<MultiOrSingleValue>>
    >;
  }): VNode | VNode[];
  selected?(props: {
    selected: InferOption<
      Multiple,
      Clearable,
      ExtractValueType<NoInfer<Multiple>, NoInfer<MultiOrSingleValue>>
    >;
  }): VNode[];
  arrow?(props: Record<string, never>): VNode | VNode[];
}>();

const name = toRef(props, 'name');
const isRequired = computed(() => props.required || props.ariaRequired);

const id = (attrs.id as string) ?? name.value;
const { dataTestid, suffix } = useDataTestid(id);
const labelId = `${id}-label`;
const selectedId = `${id}-selected`;
const descriptionId =
  props.description || slots.description || isRequired.value ? `${id}-description` : undefined;
const fallbackErrorMessageId = `${id}-error-message`;
const kaListboxButtonComponent = shallowRef<InstanceType<typeof ListboxButton>>();
const kaListboxOptionsComponent = shallowRef<InstanceType<typeof ListboxOption>>();
const kaListboxOptionsContainerElement = shallowRef<HTMLElement>();
const kaListboxClearButtonElement = shallowRef<HTMLButtonElement>();

const registeredListboxOptions = ref(new Map()) as Ref<
  Map<KaListboxOptionValueType, KaListboxOptionProps<ExtractedValueType>>
>;

const selectedValue = ref(normalizeSelectedValue(props.modelValue)) as Ref<
  MultiOrSingleValue | ExtractNullableValue<Multiple, Clearable>
>;
const optionsOpened = ref(false);

const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);
const kaListboxButtonElement = computed(() =>
  unwrapElement<HTMLButtonElement>(kaListboxButtonComponent),
);
const hasError = computed(() => props.errorMessage !== undefined);
const computedErrorMessageId = computed(() => {
  if (props.showErrorStylesOnly) return props.errorMessageId || undefined;
  return hasError.value ? fallbackErrorMessageId : undefined;
});

const selectedOptionOrOptions = computed(() => {
  if (selectedValue.value === null) {
    return null;
  }

  if (isMultiValue(selectedValue.value, multiple.value)) {
    return selectedValue.value
      ?.map((value) => registeredListboxOptions.value.get(value))
      .filter((value): value is KaListboxOptionProps<ExtractedValueType> => value !== undefined);
  }

  return registeredListboxOptions.value.get(selectedValue.value) ?? null;
});

const isClearable = computed(
  () =>
    !multiple.value &&
    selectedValue.value !== null &&
    selectedValue.value !== undefined &&
    props.clearable,
);

const { floatingStyles, isPositioned: isListReadyToRender } = useFloatingDropdown(
  kaListboxButtonElement,
  kaListboxOptionsContainerElement,
  {
    opened: optionsOpened,
    offset: 0.25,
    dropdownRect: props.optionsContainerRect,
    scrollContainer: kaListboxOptionsComponent,
    defaultPlacement: 'bottom-start',
  },
);

const optionsWidth = computed<number>(
  () => kaListboxOptionsComponent.value?.$el?.offsetWidth ?? 300,
);

// Show at most 5 options, each 40px tall
const virtualizedOptionsMinHeight = computed(() =>
  Math.min(200, (props.options?.length || 0) * 40),
);

const options = computed(() => props.options ?? []);

const optionHeights = computed(() =>
  options.value.map((option) => {
    const availableWidth = optionsWidth.value - (option?.left ? 24 : 0) - (option?.right ? 24 : 0);

    /**
     * How many pixels does a single char take.
     * This is closer to 10, but to account for line/word breaks, a larger value is used to get better approximaations
     */
    const pxPerChar = 12;
    const charsPerRow = availableWidth / pxPerChar;

    const labelRows = Math.ceil((option.label?.length || 0) / charsPerRow);
    const labelRowHeight = 20;
    const labelHeight = labelRows * labelRowHeight;

    const descriptionRows = Math.ceil((option.description?.length || 0) / charsPerRow);
    const descriptionRowHeight = 20;
    const descriptionHeight = descriptionRows * descriptionRowHeight;
    const descriptionMargin = descriptionRows ? 4 : 0;

    const itemVerticalPadding = 2 * 8;
    const itemHeight = labelHeight + descriptionHeight + descriptionMargin + itemVerticalPadding;

    return itemHeight;
  }),
);

const { list, containerProps, wrapperProps } = useVirtualList(options, {
  itemHeight: props.virtualizedItemHeight || ((index) => optionHeights.value[index] || 36),
  overscan: 10,
});

const handleUpdateModelValue = (
  val: NoInfer<MultiOrSingleValue> | ExtractNullableValue<Multiple, Clearable>,
) => {
  const newValue = normalizeSelectedValue(val);

  selectedValue.value = newValue;
  emit('update:modelValue', newValue);
  emit('change', newValue);
};

// Decides whether blur event should be dispatched or not
const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;
  // Blur didn't result in focus on something else
  if (nextTarget === null) return true;

  // If target is not an HTML Node - we can't react on it anyhow. Consider that this is blur.
  if (!(nextTarget instanceof Node)) return true;

  const clearButtonElement = unwrapElement(kaListboxClearButtonElement);
  const buttonElement = unwrapElement(kaListboxButtonComponent);
  const optionsContainer = unwrapElement(kaListboxOptionsContainerElement);

  const clearButtonIsNotFocused = !clearButtonElement || !clearButtonElement.contains(nextTarget);
  const buttonIsNotFocused = !buttonElement || !buttonElement.contains(nextTarget);
  const optionsContainerIsNotFocused = !optionsContainer || !optionsContainer.contains(nextTarget);

  return buttonIsNotFocused && optionsContainerIsNotFocused && clearButtonIsNotFocused;
};

// Handles 3 blur cases at once - options container blur, button blur and clear button blur. If none of them is focused in result -> dispatch blur event.
const handleBlur = (event: FocusEvent) => {
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);
    }
  });
};

const toggleOpened = (isOpen: boolean) => {
  optionsOpened.value = isOpen;
};

const onClearButtonClick = () => {
  handleUpdateModelValue(
    (multiple.value ? [] : null) as MultiOrSingleValue | ExtractNullableValue<Multiple, Clearable>,
  );

  kaListboxButtonElement.value?.focus({ preventScroll: true });
};

const deselect = (value: KaListboxOptionValueType, event: Event) => {
  if (selectedValue.value !== null && isMultiValue(selectedValue.value, multiple.value)) {
    handleUpdateModelValue(
      selectedValue.value.filter((s) => s !== value) as
        MultiOrSingleValue | ExtractNullableValue<Multiple, Clearable>,
    );

    event.stopPropagation();
    event.preventDefault();
    kaListboxButtonElement.value?.focus({ preventScroll: true });
  }
};

// Gather options from KaListboxOption components directly
watch(
  () => {
    // Ignore when options list is passed directly
    if (props.options !== undefined) return undefined;
    if (slots.default === undefined) return undefined;

    return slots.default({});
  },
  (nodes) => {
    if (nodes === undefined) return;

    type KaListboxOptionVNode = VNode<
      RendererNode,
      RendererElement,
      KaListboxOptionProps<ExtractedValueType>
    >;

    const isKaListboxOptionVNode = (node: VNode): node is KaListboxOptionVNode =>
      node.type === KaListboxOption;

    const findOptionNodes = (nodes: VNodeArrayChildren): KaListboxOptionVNode[] => {
      const result: KaListboxOptionVNode[] = [];
      for (const node of nodes) {
        // If node is an array - go deeper
        if (Array.isArray(node)) {
          result.push(...findOptionNodes(node));
        }
        // If node is primitive it can't be an option
        else if (!node || typeof node !== 'object' || !('type' in node)) {
          continue;
        }
        // If node is fragment and has children - go deeper
        else if (node.type === Fragment && Array.isArray(node.children)) {
          result.push(...findOptionNodes(node.children));
        }
        // If node is an option - include in result
        else if (isKaListboxOptionVNode(node)) {
          result.push(node);
        }
      }

      return result;
    };

    registeredListboxOptions.value = new Map(
      findOptionNodes(nodes).map(({ props, children }) => {
        const label =
          props!.label ??
          (children !== null
            ? // We always expect default slot from KaListboxOption if it has children
              // @ts-expect-error
              getText(children.default())
            : undefined);

        return [
          props!.value,
          {
            id: props!.id,
            label,
            description: props!.description,
            disabled: props!.disabled,
            left: props!.left,
            value: props!.value,
          } as KaListboxOptionProps<ExtractedValueType>,
        ];
      }),
    );
  },
  { immediate: true },
);

// Gather options directly from the options list
watch(
  () => props.options,
  (options) => {
    if (options === undefined) return;

    registeredListboxOptions.value = new Map(options.map((option) => [option.value, option]));
  },
  { immediate: true },
);

watch(
  () => props.modelValue,
  (val) => (selectedValue.value = normalizeSelectedValue(val)),
);

watch(optionsOpened, (isOpen) => {
  if (isOpen) {
    emit('touch');
    emit('open');
  } else {
    emit('close');
  }
});
</script>
<template>
  <Listbox
    :id="id"
    v-slot="{ open }"
    :model-value="selectedValue"
    :data-testid="dataTestid"
    :horizontal="false"
    :multiple="multiple"
    as="div"
    class="w-full"
    @update:model-value="handleUpdateModelValue"
  >
    <!--  This is intentional. Way to handle state change from inside the slot -->
    {{ toggleOpened(open) }}
    <Teleport
      v-if="open"
      :disabled="props.renderOptionsInPlace || portalContext?.teleportProps.disabled === true"
      :to="portalContext?.teleportProps.to ?? 'body'"
    >
      <div
        ref="kaListboxOptionsContainerElement"
        :class="
          cnMerge(
            'absolute left-0 top-0',
            props.optionsZIndexClass || 'z-20',
            props.optionsContainerClasses,
          )
        "
        :style="floatingStyles"
      >
        <transition
          appear
          enter-active-class="transition ease-in duration-150"
          enter-from-class="opacity-0 transform translate-y-[-5%]"
          enter-to-class="opacity-100 transform translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 transform translate-y-0"
          leave-to-class="opacity-0 transform translate-y-[-5%]"
        >
          <ListboxOptions
            ref="kaListboxOptionsComponent"
            static
            :aria-labelledby="labelId"
            :class="
              cnMerge(
                'mx-0 mb-0 divide-y divide-gray-200 rounded bg-white pl-0 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:divide-navy-600 dark:bg-navy-900 dark:text-gray-50 dark:ring-navy-600',
                { 'overflow-auto': !props.virtualized },
                props.optionsClasses,
              )
            "
            :aria-invalid="props.showErrorStylesOnly || hasError"
            :aria-errormessage="computedErrorMessageId"
            :aria-required="isRequired"
            @blur="handleBlur"
          >
            <ListboxContextProvider
              v-if="isListReadyToRender"
              :data-testid="dataTestid"
              :is-open="open"
            >
              <slot v-if="!props.options" />

              <li
                v-else-if="props.options.length === 0"
                class="cursor-pointer select-none px-4 py-2"
              >
                Sorry, no options available.
              </li>

              <div
                v-else-if="props.virtualized"
                v-bind="containerProps"
                class="h-auto max-h-[200px] overflow-auto"
                data-testid="ka-listbox-virtualized-options"
                :style="{ minHeight: `${virtualizedOptionsMinHeight}px` }"
              >
                <div v-bind="wrapperProps">
                  <template
                    v-for="option in list"
                    :key="option.data.id ?? option.data.value.toString()"
                  >
                    <slot
                      v-if="'optionItem' in slots"
                      name="optionItem"
                      :option="option.data"
                      :index="option.index"
                    />
                    <KaListboxOption v-else v-bind="option.data as any" />
                  </template>
                </div>
              </div>

              <template
                v-for="(option, index) in props.options"
                v-else
                :key="option.id ?? option.value.toString()"
              >
                <slot
                  v-if="'optionItem' in slots"
                  name="optionItem"
                  :option="option"
                  :index="index"
                />
                <KaListboxOption v-else v-bind="option as any" />
              </template>
            </ListboxContextProvider>
          </ListboxOptions>
        </transition>
      </div>
    </Teleport>

    <ListboxLabel
      :id="labelId"
      as="span"
      :data-testid="suffix('label')"
      :class="[
        'flex items-center text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
        !shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0',
        { 'sr-only': props.hideLabel },
      ]"
    >
      <slot name="label" :label="props.label">{{ props.label }}</slot>
      <span v-if="isRequired && !props.hideRequiredAsterisk">&nbsp;*</span>
    </ListboxLabel>

    <KaDescription
      v-if="props.description || $slots.description || isRequired"
      :id="descriptionId"
      :data-testid="suffix('description')"
      :hide-description="
        props.hideDescription || (!props.description && !$slots.description && isRequired)
      "
      :class="shouldDisplayDescription ? 'mx-0 mb-3 mt-0' : 'm-0'"
    >
      <slot name="description" v-bind="{ description: props.description }">
        <span>{{ props.description }}</span>
      </slot>

      <span v-if="isRequired" class="sr-only">
        {{ props.label || 'This' }} is a required field.
      </span>
    </KaDescription>

    <div class="w-full min-w-[160px]">
      <div class="relative w-full">
        <ListboxButton
          :id="id + '-button'"
          ref="kaListboxButtonComponent"
          :aria-labelledby="labelId"
          :aria-describedby="descriptionId"
          :aria-invalid="props.showErrorStylesOnly || hasError"
          :class="
            cnMerge(
              'relative flex min-h-[36px] w-full cursor-pointer items-center rounded bg-white py-1.5 pl-3 text-left text-sm leading-6 text-gray-900 shadow-sm ring-1 ring-inset focus:outline-none focus:ring-2 dark:bg-navy-800 dark:text-gray-50',
              isClearable ? (props.hideArrow ? 'pr-10' : 'pr-14') : 'pr-10',
              {
                'ring-blue-400 dark:ring-blue-500': open && !hasError && !props.showErrorStylesOnly,
                'ring-red-500/60 focus:ring-red-500/80': props.showErrorStylesOnly || hasError,
                'ring-gray-300 focus:ring-blue-400 dark:ring-navy-600 dark:focus:ring-blue-500':
                  !open && !hasError && !props.showErrorStylesOnly,
              },
              props.buttonClasses,
            )
          "
          :data-testid="suffix('button')"
          @blur="handleBlur"
        >
          <span
            v-if="$slots.left || props.left"
            class="mr-2 flex size-6 flex-shrink-0 flex-grow-0 items-center justify-center [&>svg]:block [&>svg]:align-baseline"
            aria-hidden="true"
          >
            <slot
              name="left"
              :selected="
                selectedOptionOrOptions as InferOption<Multiple, Clearable, ExtractedValueType>
              "
            >
              <component
                :is="props.left.icon"
                v-if="props.left"
                :class="cnMerge('size-3.5', props.left.classes)"
              />
            </slot>
          </span>

          <slot
            name="selected"
            :selected="
              selectedOptionOrOptions as InferOption<Multiple, Clearable, ExtractedValueType>
            "
          >
            <div
              v-if="isMultiValue(selectedOptionOrOptions, multiple)"
              class="flex flex-wrap gap-1"
            >
              <KaBadge
                v-for="selectedOption in selectedOptionOrOptions"
                :key="`${selectedOption.value}-selected-listbox-badge`"
                :data-testid="suffix(`selected-${selectedOption.label}`)"
                :color="props.selectedBadgeColor"
                :left="selectedOption.left"
                :right="selectedOption.right"
                removeable
                @remove:click="(event) => deselect(selectedOption.value, event)"
                @remove:space="(event) => deselect(selectedOption.value, event)"
                @remove:enter="(event) => deselect(selectedOption.value, event)"
                @remove:blur="handleBlur"
              >
                {{ selectedOption.label }}
              </KaBadge>
            </div>

            <span
              v-else
              :id="selectedId"
              :data-testid="suffix('selected')"
              class="mr-2 block w-full truncate"
              :title="selectedOptionOrOptions?.label"
            >
              {{ selectedOptionOrOptions?.label }}
            </span>
          </slot>

          <span
            v-if="'right' in slots || props.right"
            :data-testid="suffix('right')"
            class="mr-3 inline-flex flex-shrink-0 flex-grow-0 items-center justify-center text-current [&>svg]:block [&>svg]:align-baseline"
            aria-hidden="true"
          >
            <slot
              name="right"
              :selected="
                selectedOptionOrOptions as InferOption<Multiple, Clearable, ExtractedValueType>
              "
            >
              <component
                :is="props.right.icon"
                v-if="props.right"
                :class="cnMerge('size-3.5', props.right.classes)"
              />
            </slot>
          </span>

          <span
            v-if="!props.hideArrow"
            :data-testid="suffix('arrow')"
            class="absolute inset-y-0 right-0 flex items-center justify-center pr-2 text-gray-600 outline-none dark:text-gray-500 [&>svg]:block [&>svg]:align-baseline"
            aria-hidden="true"
          >
            <slot name="arrow">
              <component
                :is="props.arrow.icon"
                v-if="props.arrow"
                :class="cnMerge('size-5', props.arrow.classes)"
              />

              <ChevronUpDownIcon v-else class="size-5" />
            </slot>
          </span>
        </ListboxButton>

        <div
          v-if="isClearable"
          :class="[
            'absolute inset-y-0 flex cursor-default items-center',
            props.hideArrow ? 'right-3' : 'right-8',
          ]"
        >
          <button
            ref="kaListboxClearButtonElement"
            type="button"
            :class="
              cnMerge(
                'flex items-center rounded text-gray-900 transition-colors hover:bg-black/5 focus:outline focus:outline-2 focus:outline-blue-400 dark:text-gray-50 dark:hover:bg-white/5',
                props.clearButtonClasses,
              )
            "
            title="Clear selected"
            aria-label="Clear selected"
            @click.prevent.stop="onClearButtonClick"
            @blur="handleBlur"
          >
            <IconClose class="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>

    <KaMessage
      v-if="!props.showErrorStylesOnly && hasError"
      :id="fallbackErrorMessageId"
      :data-testid="suffix('error-message')"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      :class="['mt-2', { 'sr-only': props.hideErrorMessage }] as VueClass"
    />
  </Listbox>
</template>
